import { motion, type MotionProps } from "framer-motion";
import type React from "react";

/**
 * framer-motion's element props don't resolve against this repo's React 19 types
 * — `motion.div` ends up with unresolved generics and rejects `className` (see
 * components/global/sections/LastCTA.tsx for the same breakage untouched).
 * These aliases put the DOM props back on the tags we use. `style` and `children`
 * are dropped from the DOM side so framer's own types win for both: `style` needs
 * to accept MotionValues, and a MotionValue passed as the sole child is rendered
 * straight onto textContent, which `ReactNode` would reject.
 */
export type MotionTagProps<T extends keyof React.JSX.IntrinsicElements> = Omit<
  React.ComponentPropsWithoutRef<T>,
  "style" | "children"
> &
  MotionProps;

type MotionTag<
  T extends keyof React.JSX.IntrinsicElements,
  E,
> = React.ForwardRefExoticComponent<
  MotionTagProps<T> & React.RefAttributes<E>
>;

export const MotionDiv = motion.div as unknown as MotionTag<
  "div",
  HTMLDivElement
>;

export const MotionP = motion.p as unknown as MotionTag<
  "p",
  HTMLParagraphElement
>;

export const MotionSpan = motion.span as unknown as MotionTag<
  "span",
  HTMLSpanElement
>;
